import { useEffect, useState } from "react";
import { api } from "../../lib/api";

/** Módulos activables por organización (clave = la de MODULO_POR_RUTA en tienda.tsx). */
const MODULOS: { key: string; label: string }[] = [
  { key: "delivery", label: "Delivery" },
  { key: "pagos_reservas", label: "Pagos de reserva" },
  { key: "encuestas", label: "Encuestas" },
  { key: "eventos", label: "Eventos" },
  { key: "audiencias", label: "Audiencias" },
  { key: "cliente360", label: "Cliente 360°" },
  { key: "reportes", label: "Reportes" },
  { key: "whatsapp", label: "WhatsApp" },
  { key: "qr", label: "Códigos QR" },
  { key: "calendario", label: "Calendario" },
];

interface Org {
  id: number;
  nombre: string;
  modulos: Record<string, boolean>;
}

export function ModulosGlobalPage() {
  const [orgs, setOrgs] = useState<Org[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [savedId, setSavedId] = useState<number | null>(null);

  useEffect(() => {
    api<Org[]>("/v1/sucursales/admin/organizaciones-modulos")
      .then((d) => setOrgs((d ?? []).map((o) => ({ ...o, modulos: o.modulos ?? {} }))))
      .catch(() => setOrgs([]))
      .finally(() => setLoading(false));
  }, []);

  const activo = (o: Org, k: string) => o.modulos[k] !== false;

  const toggle = (orgId: number, key: string) =>
    setOrgs((prev) =>
      prev.map((o) =>
        o.id === orgId ? { ...o, modulos: { ...o.modulos, [key]: !(o.modulos[key] !== false) } } : o,
      ),
    );

  const guardar = async (org: Org) => {
    setSavingId(org.id);
    setSavedId(null);
    try {
      await api(`/v1/sucursales/admin/organizaciones/${org.id}/modulos`, {
        method: "PUT",
        body: { modulos: org.modulos },
      });
      setSavedId(org.id);
      setTimeout(() => setSavedId(null), 2500);
    } finally {
      setSavingId(null);
    }
  };

  if (loading) {
    return <div className="mx-auto max-w-4xl px-4 py-8 text-ink-500">Cargando organizaciones…</div>;
  }

  return (
    <div className="mx-auto max-w-4xl px-4 sm:px-6 py-8 animate-fade-in">
      <h1 className="font-display text-2xl font-bold text-ink-800 mb-1">Módulos por cliente</h1>
      <p className="text-sm text-ink-500 mb-6">
        Activá o desactivá los módulos de cada organización desde un solo lugar. Los cambios aplican
        cuando sus usuarios vuelven a iniciar sesión.
      </p>

      <div className="space-y-5">
        {orgs.map((org) => (
          <div key={org.id} className="card p-5">
            <div className="flex items-center justify-between mb-3 gap-3">
              <h2 className="font-semibold text-ink-800 truncate">
                {org.nombre} <span className="text-xs text-ink-400">#{org.id}</span>
              </h2>
              <button onClick={() => guardar(org)} disabled={savingId === org.id}
                className="btn-primary text-sm px-4 shrink-0">
                {savingId === org.id ? "Guardando…" : savedId === org.id ? "✓ Guardado" : "Guardar"}
              </button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-y-2.5 gap-x-4">
              {MODULOS.map((m) => (
                <label key={m.key} className="flex items-center gap-2 cursor-pointer text-sm">
                  <div onClick={() => toggle(org.id, m.key)}
                    className={`w-9 h-5 rounded-full transition-colors relative shrink-0 ${activo(org, m.key) ? "bg-brand-600" : "bg-ink-300"}`}>
                    <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${activo(org, m.key) ? "translate-x-4" : "translate-x-0"}`} />
                  </div>
                  <span className="text-ink-700">{m.label}</span>
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
